import type { IntakeField } from './flow.js'

/**
 * Which field holds her name, and which one holds a way to reach her — with NO
 * runtime dependencies.
 *
 * Its own module for the reason `./intake.ts` is: the customer client imports
 * nothing but types from this package, and pulling this through the barrel
 * would drag every zod schema in it into a bundle a bride loads on her phone.
 *
 * The rule lives in the contract rather than on either side because BOTH sides
 * need the same answer about the same flow. The client writes a walk-in's name
 * and address into these fields; the staff console reads them back out of her
 * profile onto the enquiry list. Two implementations of "which field is the
 * address" is two answers waiting to differ, and the day they do, the form
 * writes to one field and the list reads another — a customer who filled
 * everything in, showing up on a stylist's screen as nameless.
 */

/**
 * A field that holds an email address.
 *
 * `kind` is the contract's own word for it and is already load-bearing: it
 * picks the keyboard her phone puts under her thumb.
 */
export function isAddressField(field: IntakeField): boolean {
  return field.kind === 'email'
}

/** A field that holds a phone number, by the same argument. */
export function isPhoneField(field: IntakeField): boolean {
  return field.kind === 'tel'
}

/**
 * A field that holds her name.
 *
 * There is no `name` kind — a name is text, and so are eleven other things —
 * so this asks the nearest thing the contract has to a field declaring what it
 * holds: the booking column the server fills it from. Only a field that holds
 * her name may be prefilled from the customer's name, so a field that says it
 * is, is one.
 *
 * The cost, and it is deliberate: a flow whose name field declares no prefill
 * is not found. The honest response to that is to ask for nothing rather than
 * to take whichever text box came first — a venue or a nickname arriving on a
 * pull sheet under "name" is worse than an empty line.
 */
export function isNameField(field: IntakeField): boolean {
  return field.prefillFrom === 'customerName'
}

/**
 * A field that holds the wedding date: the one that declares it fills from,
 * and so stands for, the booking's wedding date. By the argument above —
 * a flow may ask for several dates, and the first date box is not
 * necessarily the wedding.
 */
export function isWeddingDateField(field: IntakeField): boolean {
  return field.prefillFrom === 'weddingDate'
}
